import { type fieldSchemaDefV1 } from '../../../common/fieldSchemaDef';
import { confirmNavigationAtom } from '../../atoms/confirmNavigationAtom';
import { trpc } from '../../lib/trpc';
import withParams from '../../lib/withParams';
import { DraggableRow } from './DraggableRow';
import { FieldWizard } from './FieldWizard';
import { useModelDeleteDialog } from './useModelDeleteDialog';
import { useRename } from './useRename';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ActionIcon,
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Flex,
  Menu,
  Switch,
  Table,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { cloneDeep, invariant, isEqual, pick } from 'es-toolkit';
import { useSetAtom } from 'jotai';
import {
  BadgeAlert,
  BadgeCheck,
  EllipsisVertical,
  FilePen,
  Plus,
  Trash2,
} from 'lucide-react';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { ulid } from 'ulid';
import { Link } from 'wouter';
import { z } from 'zod';

const paramsSchema = z.object({ id: z.string() });

export const Model = withParams(({ id }) => {
  const data = use(
    trpc.models.show.useQuery(id, {
      experimental_prefetchInRender: true,
    }).promise,
  );
  invariant(data, 'data is empty');

  const [ctProps, setCtProps] = useState({
    isTitleSlugEnabled: data.isTitleSlugEnabled,
  });
  const [rows, setRows] = useState(data.schema.fields);
  useEffect(() => {
    setCtProps({ isTitleSlugEnabled: data.isTitleSlugEnabled });
    setRows(data?.schema.fields);
  }, [data]);
  const { initiateModelDelete } = useModelDeleteDialog({
    id,
  });
  const { initiateRename } = useRename({ id });
  const { mutateAsync: updateModel } = trpc.models.update.useMutation();
  const utils = trpc.useUtils();

  const modalId = ulid();
  const openAddFieldDialog = useCallback(
    () =>
      modals.open({
        children: (
          <FieldWizard
            onSubmit={async (value) => {
              if (data.schema) {
                setRows((current) => [...(current || []), value]);
                modals.close(modalId);
              }
            }}
          />
        ),
        modalId,
        size: 'lg',
        title: <strong>Configure the field</strong>,
      }),
    [data.schema, modalId],
  );

  const saveSchema = async () => {
    try {
      if (data.schema && rows) {
        const newSchema = cloneDeep(data.schema);
        newSchema.fields = rows;
        await updateModel({
          data: { ...ctProps, schema: newSchema },
          id,
        });
        utils.models.show.invalidate();
        notifications.show({
          message: (
            <>
              <BadgeCheck stroke="var(--color-lime-400)" /> Saved.
            </>
          ),
        });
      }
    } catch {
      notifications.show({
        message: (
          <>
            <BadgeAlert stroke="var(--color-red-400)" /> An error occurred.
            Please try again.
          </>
        ),
      });
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  // const originalRowIds = useMemo<UniqueIdentifier[]>(
  //   () => data.schema.fields.map((field) => field.id) || [],
  //   [data.schema.fields],
  // );

  const currenrRowIds = useMemo<UniqueIdentifier[]>(
    () => rows?.map((field) => field.id) || [],
    [rows],
  );

  const isDirty =
    !isEqual(data.schema.fields, rows) ||
    !isEqual(
      ctProps,
      pick(data, Object.keys(ctProps) as unknown[] as Array<keyof typeof data>),
    );

  const setConfirmNavigationAway = useSetAtom(confirmNavigationAtom);
  useEffect(() => {
    setConfirmNavigationAway(isDirty);
  }, [isDirty, setConfirmNavigationAway]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setRows((currentRows) => {
        if (!currentRows) {
          return currentRows;
        }

        const oldIndex = currenrRowIds.indexOf(active.id);
        const newIndex = currenrRowIds.indexOf(over.id);
        return arrayMove(currentRows, oldIndex, newIndex); // this is just a splice util
      });
    }
  };

  const deleteHandler = (fieldId: string) => {
    setRows((current) => {
      if (!current) {
        return current;
      }

      const toDeleteIdx = current.findIndex((field) => field.id === fieldId);
      if (toDeleteIdx === -1) {
        return current;
      }

      const newValue = cloneDeep(current);
      newValue.splice(toDeleteIdx, 1);

      return newValue;
    });
  };

  const updateHandler = (
    updateFieldData: z.infer<typeof fieldSchemaDefV1.shape.fields.element>,
  ) => {
    setRows((current) => {
      if (!current) {
        return current;
      }

      const toUpdateIdx = current.findIndex(
        (field) => field.id === updateFieldData.id,
      );
      if (toUpdateIdx === -1) {
        return current;
      }

      const newValue = cloneDeep(current);
      newValue.splice(toUpdateIdx, 1, updateFieldData);

      return newValue;
    });
  };

  return (
    <div className="w-full relative p-5">
      <Breadcrumbs className="mb-5">
        {[
          <Anchor
            component={Link}
            key="models"
            to="/models"
          >
            Models
          </Anchor>,
          <Text key="bar">{data.name}</Text>,
        ]}
      </Breadcrumbs>

      {/* {data ? JSON.stringify(data) : 'loading...'} */}
      <Box pos="relative">
        {/* <LoadingOverlay
          visible={formState.isSubmitting}
          // zIndex={1_000}
        /> */}
        <Flex gap="xs">
          <Title
            className="grow"
            order={3}
          >
            Fields
            {/* <ActionIcon onClick={openAddFieldDialog}>
              <Plus />
            </ActionIcon> */}
          </Title>
          <Button
            disabled={!isDirty}
            onClick={saveSchema}
            size="compact-md"
          >
            Save
          </Button>
          <Menu
            shadow="md"
            width={200}
          >
            <Menu.Target>
              <ActionIcon
                size="md"
                variant="subtle"
              >
                <EllipsisVertical />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<FilePen />}
                onClick={initiateRename}
              >
                Rename
              </Menu.Item>
              <Menu.Label>Danger zone</Menu.Label>
              <Menu.Item
                color="red"
                leftSection={<Trash2 />}
                onClick={initiateModelDelete}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>

        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <Table classNames={{ td: 'py-2 border-b border-gray-300' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>name</Table.Th>
                <Table.Th>type</Table.Th>
                <Table.Th colSpan={2} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td />
                <Table.Td>Title</Table.Td>
                <Table.Td colSpan={3}>
                  <div className="flex flex-row gap-5">
                    text
                    <Switch
                      checked={Boolean(ctProps.isTitleSlugEnabled)}
                      label="Slugify"
                      onChange={() =>
                        setCtProps((current) => ({
                          ...current,
                          isTitleSlugEnabled: !current.isTitleSlugEnabled,
                        }))
                      }
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
              <SortableContext
                items={currenrRowIds}
                strategy={verticalListSortingStrategy}
              >
                {rows?.map((fieldDef) => (
                  <DraggableRow
                    deleteHandler={deleteHandler}
                    fieldDef={fieldDef}
                    key={fieldDef.id}
                    updateHandler={updateHandler}
                  />
                ))}
              </SortableContext>
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr className="[&_td]:p-0">
                <Table.Td
                  className="hover:bg-slate-200"
                  colSpan={5}
                >
                  <UnstyledButton
                    className="w-full leading-10 text-sm flex flex-row justify-center items-center gap-1"
                    onClick={openAddFieldDialog}
                  >
                    <Plus />
                    <div>Add field</div>
                  </UnstyledButton>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </DndContext>
      </Box>
    </div>
  );
}, paramsSchema);
